import { Instance, cast, flow, getRoot, types } from "mobx-state-tree";
import {
  TranslationUploadStore,
  createTranslationUploadStore,
} from "./TranslationUploadStore";
import {
  SurveyLanguageModel,
  createSurveyLanguageModel,
} from "@pulse/shared-components";
import {
  SurveyTranslationTextModel,
  createSurveyTranslationTextModel,
} from "../models/SurveyTranslationTextModel";
import { RootStore } from "../../root/store/RootStore";
import { getAPIClient } from "../../networking/APIClient";
import { SurveyLanguage } from "@pulse/pulse-rpcs/lib/types/surveyLanguage";
import { SurveyLanguageCode } from "@pulse/pulse-rpcs/lib/types/surveyLanguageCode";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import {
  useDeleteSurveyLanguageRPCClientImpl,
  useGetAllTranslationsRPCClientImpl,
  useRenameSurveyLanguageRPCClientImpl,
  useUpdateLanguageConfigurationRPCClientImpl,
} from "../rpcs/RPC";
import {
  SurveyLanguageListWithTabIndexModel,
  createSurveyLanguageListWithTabIndexModel,
} from "../models/SurveyLanguageListWithTabIndexModel";
import { TabProps } from "@surya-digital/leo-reactjs-material-ui";
import { GetAllTranslationsRPC } from "@pulse/pulse-rpcs/lib/survey/getAllTranslationsRPC";
import { UpdateLanguageConfigurationRPC } from "@pulse/pulse-rpcs/lib/survey/updateLanguageConfigurationRPC";
import { RenameSurveyLanguageRPC } from "@pulse/pulse-rpcs/lib/survey/renameSurveyLanguageRPC";
import { DeleteSurveyLanguageRPC } from "@pulse/pulse-rpcs/lib/survey/deleteSurveyLanguageRPC";

export enum TranslationType {
  FirstTranslation = "FIRST_TRANSLATION",
  SpecificTranslation = "SPECIFIC_TRANSLATION",
}

export enum TranslationUpdateStatus {
  InProgress = "IN_PROGRESS",
  NoTranslationsFound = "NO_TRANSLATIONS_FOUND",
  TranslationData = "TRANSLATION_DATA",
  IsLoading = "IS_LOADING",
  IsError = "IS_ERROR",
}

export enum GetAllTranslationsError {
  InvalidLanguageId = "INVALID_LANGUAGE_ID",
  BaseTranslationNotFound = "BASE_TRANSLATION_NOT_FOUND",
}

export enum UpdateLanguageConfigurationError {
  InvalidProjectId = "INVALID_PROJECT_ID",
  InvalidSurveyId = "INVALID_SURVEY_ID",
  InvalidLanguageId = "INVALID_LANGUAGE_ID",
  ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
  TranslationUpdateInProgress = "TRANSLATION_UPDATE_IN_PROGRESS",
}

export enum RenameSurveyLanguageError {
  InvalidProjectId = "INVALID_PROJECT_ID",
  InvalidSurveyId = "INVALID_SURVEY_ID",
  InvalidLanguageId = "INVALID_LANGUAGE_ID",
  InvalidLanguageCode = "INVALID_LANGUAGE_CODE",
  BaseLanguageCannotBeModified = "BASE_LANGUAGE_CANNOT_BE_MODIFIED",
  DuplicateLanguageCode = "DUPLICATE_LANGUAGE_CODE",
  ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
  TranslationUpdateInProgress = "TRANSLATION_UPDATE_IN_PROGRESS",
  Unexpected = "UNEXPECTED",
}

export enum DeleteSurveyLanguageError {
  InvalidProjectId = "INVALID_PROJECT_ID",
  InvalidSurveyId = "INVALID_SURVEY_ID",
  InvalidLanguageId = "INVALID_LANGUAGE_ID",
  BaseLanguageCannotBeModified = "BASE_LANGUAGE_CANNOT_BE_MODIFIED",
  LanguageInUse = "LANGUAGE_IN_USE",
  ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
  TranslationUpdateInProgress = "TRANSLATION_UPDATE_IN_PROGRESS",
  Unexpected = "UNEXPECTED",
}

export const TranslationsStore = types
  .model("TranslationsStore", {
    translationUploadStore: TranslationUploadStore,
    translationType: types.optional(
      types.enumeration("TranslationType", Object.values(TranslationType)),
      TranslationType.FirstTranslation,
    ),
    translationUpdateStatus: types.optional(
      types.enumeration(
        "TranslationUpdateStatus",
        Object.values(TranslationUpdateStatus),
      ),
      TranslationUpdateStatus.IsLoading,
    ),
    surveyLanguageList: types.array(SurveyLanguageListWithTabIndexModel),
    baseLanguage: types.maybeNull(SurveyLanguageModel),
    translatedLanguage: types.maybeNull(SurveyLanguageModel),
    selectedTranslationText: types.array(SurveyTranslationTextModel),
    rpcErrors: types.maybeNull(
      types.enumeration(
        "GetAllTranslationsError",
        Object.values(GetAllTranslationsError),
      ),
    ),
    selectedLanguage: types.maybeNull(SurveyLanguageListWithTabIndexModel),
    isUpdatingLanguageConfiguration: types.optional(types.boolean, false),
    updateLanguageConfigurationError: types.maybeNull(
      types.enumeration(
        "UpdateLanguageConfigurationError",
        Object.values(UpdateLanguageConfigurationError),
      ),
    ),
    isRenamingSurveyLanguage: types.optional(types.boolean, false),
    renameSurveyLanguageError: types.maybeNull(
      types.enumeration(
        "RenameSurveyLanguageError",
        Object.values(RenameSurveyLanguageError),
      ),
    ),
    isDeletingSurveyLanguage: types.optional(types.boolean, false),
    deleteSurveyLanguageError: types.maybeNull(
      types.enumeration(
        "DeleteSurveyLanguageError",
        Object.values(DeleteSurveyLanguageError),
      ),
    ),
  })
  .views((store) => ({
    get isSingleLanguagePresent(): boolean {
      return store.surveyLanguageList.length === 1;
    },
    get requestTranslationType():
      | GetAllTranslationsRPC.RequestEnums.TranslationType.TranslationType
      | undefined {
      switch (store.translationType) {
        case TranslationType.FirstTranslation: {
          return new GetAllTranslationsRPC.RequestEnums.TranslationType.FirstTranslation();
        }
        case TranslationType.SpecificTranslation: {
          if (store.selectedLanguage === null) {
            console.error("The selected language cannot be null.");
            return;
          }
          return new GetAllTranslationsRPC.RequestEnums.TranslationType.SpecificTranslation(
            new LeoUUID(store.selectedLanguage.surveyLanguage.languageId),
          );
        }
      }
    },
    get isRPCLoading(): boolean {
      return (
        store.translationUpdateStatus === TranslationUpdateStatus.IsLoading
      );
    },
    isLastTranslationElement(index: number): boolean {
      return store.selectedTranslationText.length - 1 === index;
    },
    get translationListTabs(): TabProps[] {
      return store.surveyLanguageList.map((surveyLanguage) => {
        return {
          label: surveyLanguage.surveyLanguage.languageCode,
        };
      });
    },
    get isUploadTranslationButtonVisible(): boolean {
      return store.translationUpdateStatus !== TranslationUpdateStatus.IsError;
    },
    get isLanguageActionInProgress(): boolean {
      return (
        store.isUpdatingLanguageConfiguration ||
        store.isRenamingSurveyLanguage ||
        store.isDeletingSurveyLanguage
      );
    },
  }))
  .actions((store) => ({
    resetStore: (): void => {
      store.rpcErrors = null;
      store.translationType = TranslationType.FirstTranslation;
      store.translationUpdateStatus = TranslationUpdateStatus.IsLoading;
      store.selectedLanguage = null;
      store.selectedTranslationText.clear();
      store.baseLanguage = null;
      store.translatedLanguage = null;
      store.surveyLanguageList.clear();
      store.translationUploadStore = createTranslationUploadStore();
      store.isUpdatingLanguageConfiguration = false;
      store.updateLanguageConfigurationError = null;
      store.isRenamingSurveyLanguage = false;
      store.renameSurveyLanguageError = null;
      store.isDeletingSurveyLanguage = false;
      store.deleteSurveyLanguageError = null;
    },
    resetRenameSurveyLanguageState: (): void => {
      store.renameSurveyLanguageError = null;
      store.isRenamingSurveyLanguage = false;
    },
    resetUpdateLanguageConfigurationState: (): void => {
      store.updateLanguageConfigurationError = null;
      store.isUpdatingLanguageConfiguration = false;
    },
    resetDeleteSurveyLanguageState: (): void => {
      store.deleteSurveyLanguageError = null;
      store.isDeletingSurveyLanguage = false;
    },
    selectFirstTranslation: (): void => {
      store.translationType = TranslationType.FirstTranslation;
      store.selectedLanguage = null;
    },
    setSelectedLanguageBasedOnIndex: (index: number): void => {
      store.translationType = TranslationType.SpecificTranslation;
      const selectedLanguage = store.surveyLanguageList.find(
        (surveyLanguage) => surveyLanguage.tabIndex === index,
      );
      if (selectedLanguage === undefined) {
        console.error("The selected language was not found.");
        return;
      } else {
        store.selectedLanguage = createSurveyLanguageListWithTabIndexModel(
          selectedLanguage.tabIndex,
          new SurveyLanguage(
            new LeoUUID(selectedLanguage.surveyLanguage.languageId),
            new SurveyLanguageCode(
              selectedLanguage.surveyLanguage.languageCode,
            ),
            selectedLanguage.surveyLanguage.isRTL,
          ),
        );
      }
    },
    getAllTranslations: flow(function* (surveyId: string, projectId: string) {
      store.translationUpdateStatus = TranslationUpdateStatus.IsLoading;
      try {
        const apiClient = getAPIClient(store);
        const requestTranslationType = store.requestTranslationType;
        if (requestTranslationType === undefined) {
          console.error("TranslationType was found to be undefined.");
          store.translationUpdateStatus = TranslationUpdateStatus.IsError;
          return;
        }
        const request = new GetAllTranslationsRPC.Request(
          new LeoUUID(surveyId),
          new LeoUUID(projectId),
          requestTranslationType,
        );

        const {
          response,
          error,
        }: {
          response?: GetAllTranslationsRPC.Response;
          error?: GetAllTranslationsRPC.Errors.Errors;
        } =
          yield useGetAllTranslationsRPCClientImpl(apiClient).execute(request);

        if (response) {
          const translationUpdateStatus = response.translationUpdateStatus;
          if (
            translationUpdateStatus instanceof
            GetAllTranslationsRPC.ResponseEnums.TranslationUpdateStatus
              .InProgress
          ) {
            store.translationUpdateStatus = TranslationUpdateStatus.InProgress;
          } else if (
            translationUpdateStatus instanceof
            GetAllTranslationsRPC.ResponseEnums.TranslationUpdateStatus
              .NoTranslationsFound
          ) {
            store.translationUpdateStatus =
              TranslationUpdateStatus.NoTranslationsFound;
            store.baseLanguage = null;
            store.translatedLanguage = null;
            store.selectedLanguage = null;
            store.surveyLanguageList.clear();
            store.selectedTranslationText.clear();
          } else if (
            translationUpdateStatus instanceof
            GetAllTranslationsRPC.ResponseEnums.TranslationUpdateStatus
              .TranslationData
          ) {
            store.translationUpdateStatus =
              TranslationUpdateStatus.TranslationData;
            store.baseLanguage = createSurveyLanguageModel(
              translationUpdateStatus.baseLanguage,
            );
            store.translatedLanguage = createSurveyLanguageModel(
              translationUpdateStatus.translatedLanguage,
            );
            store.surveyLanguageList = cast(
              translationUpdateStatus.surveyLanguageList.map(
                (surveyLanguage, index) => {
                  return createSurveyLanguageListWithTabIndexModel(
                    index,
                    surveyLanguage,
                  );
                },
              ),
            );
            if (store.selectedLanguage !== null) {
              const selectedLanguageId =
                store.selectedLanguage.surveyLanguage.languageId;
              const refreshedSelectedLanguage = store.surveyLanguageList.find(
                ({ surveyLanguage }) =>
                  surveyLanguage.languageId === selectedLanguageId,
              );
              if (refreshedSelectedLanguage !== undefined) {
                store.selectedLanguage =
                  createSurveyLanguageListWithTabIndexModel(
                    refreshedSelectedLanguage.tabIndex,
                    new SurveyLanguage(
                      new LeoUUID(
                        refreshedSelectedLanguage.surveyLanguage.languageId,
                      ),
                      new SurveyLanguageCode(
                        refreshedSelectedLanguage.surveyLanguage.languageCode,
                      ),
                      refreshedSelectedLanguage.surveyLanguage.isRTL,
                    ),
                  );
              }
            }
            store.selectedTranslationText = cast(
              translationUpdateStatus.selectedTranslationTexts.map(
                (translationText) => {
                  return createSurveyTranslationTextModel(translationText);
                },
              ),
            );
          }
        } else if (error) {
          switch (error.code) {
            case GetAllTranslationsRPC.RPCError.InvalidSurveyId:
            case GetAllTranslationsRPC.RPCError.InvalidProjectId: {
              break;
            }
            case GetAllTranslationsRPC.RPCError.BaseTranslationNotFound: {
              store.translationUpdateStatus = TranslationUpdateStatus.IsError;
              store.rpcErrors = GetAllTranslationsError.BaseTranslationNotFound;
              break;
            }
            case GetAllTranslationsRPC.RPCError.InvalidLanguageId: {
              store.translationUpdateStatus = TranslationUpdateStatus.IsError;
              store.rpcErrors = GetAllTranslationsError.InvalidLanguageId;
              break;
            }
            default: {
              store.translationUpdateStatus = TranslationUpdateStatus.IsError;
              console.error(`Unexpected error occured: ${error}`);
            }
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          store.translationUpdateStatus = TranslationUpdateStatus.IsError;
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occured: ${e}`);
        }
      }
    }),
    updateLanguageConfiguration: flow(function* (
      surveyId: string,
      projectId: string,
      languageId: string,
      isRTL: boolean,
    ) {
      store.isUpdatingLanguageConfiguration = true;
      store.updateLanguageConfigurationError = null;
      try {
        const apiClient = getAPIClient(store);
        const request = new UpdateLanguageConfigurationRPC.Request(
          new LeoUUID(projectId),
          new LeoUUID(surveyId),
          new LeoUUID(languageId),
          isRTL,
        );

        const {
          response,
          error,
        }: {
          response?: UpdateLanguageConfigurationRPC.Response;
          error?: UpdateLanguageConfigurationRPC.Errors.Errors;
        } =
          yield useUpdateLanguageConfigurationRPCClientImpl(apiClient).execute(
            request,
          );

        if (response) {
          // On success, do nothing. The translations data will be refreshed in the handler.
        } else if (error) {
          switch (error.code) {
            case UpdateLanguageConfigurationRPC.RPCError.InvalidProjectId: {
              store.updateLanguageConfigurationError =
                UpdateLanguageConfigurationError.InvalidProjectId;
              break;
            }
            case UpdateLanguageConfigurationRPC.RPCError.InvalidSurveyId: {
              store.updateLanguageConfigurationError =
                UpdateLanguageConfigurationError.InvalidSurveyId;
              break;
            }
            case UpdateLanguageConfigurationRPC.RPCError.InvalidLanguageId: {
              store.updateLanguageConfigurationError =
                UpdateLanguageConfigurationError.InvalidLanguageId;
              break;
            }
            case UpdateLanguageConfigurationRPC.RPCError
              .ProjectAlreadyArchived: {
              store.updateLanguageConfigurationError =
                UpdateLanguageConfigurationError.ProjectAlreadyArchived;
              break;
            }
            case UpdateLanguageConfigurationRPC.RPCError
              .TranslationUpdateInProgress: {
              store.updateLanguageConfigurationError =
                UpdateLanguageConfigurationError.TranslationUpdateInProgress;
              break;
            }
            default: {
              console.error(`Unexpected error occurred: ${error}`);
              const rootStore = getRoot<typeof RootStore>(store);
              rootStore.networkingStore.errorStore.setLeoError(
                new Error(`Unexpected error: ${error.code}.`),
              );
            }
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occurred: ${e}`);
        }
      } finally {
        store.isUpdatingLanguageConfiguration = false;
      }
    }),
    renameSurveyLanguage: flow(function* (
      surveyId: string,
      projectId: string,
      languageId: string,
      languageCode: string,
    ) {
      store.isRenamingSurveyLanguage = true;
      store.renameSurveyLanguageError = null;
      try {
        const apiClient = getAPIClient(store);
        const request = new RenameSurveyLanguageRPC.Request(
          new LeoUUID(projectId),
          new LeoUUID(surveyId),
          new LeoUUID(languageId),
          new SurveyLanguageCode(languageCode),
        );
        const {
          error,
        }: {
          response?: RenameSurveyLanguageRPC.Response;
          error?: RenameSurveyLanguageRPC.Errors.Errors;
        } =
          yield useRenameSurveyLanguageRPCClientImpl(apiClient).execute(
            request,
          );

        if (error) {
          switch (error.code) {
            case RenameSurveyLanguageRPC.RPCError.InvalidProjectId:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.InvalidProjectId;
              break;
            case RenameSurveyLanguageRPC.RPCError.InvalidSurveyId:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.InvalidSurveyId;
              break;
            case RenameSurveyLanguageRPC.RPCError.InvalidLanguageId:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.InvalidLanguageId;
              break;
            case RenameSurveyLanguageRPC.RPCError.InvalidLanguageCode:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.InvalidLanguageCode;
              break;
            case RenameSurveyLanguageRPC.RPCError.BaseLanguageCannotBeModified:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.BaseLanguageCannotBeModified;
              break;
            case RenameSurveyLanguageRPC.RPCError.DuplicateLanguageCode:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.DuplicateLanguageCode;
              break;
            case RenameSurveyLanguageRPC.RPCError.ProjectAlreadyArchived:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.ProjectAlreadyArchived;
              break;
            case RenameSurveyLanguageRPC.RPCError.TranslationUpdateInProgress:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.TranslationUpdateInProgress;
              break;
            default:
              store.renameSurveyLanguageError =
                RenameSurveyLanguageError.Unexpected;
          }
        }
      } catch (e) {
        store.renameSurveyLanguageError = RenameSurveyLanguageError.Unexpected;
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occurred: ${e}`);
        }
      } finally {
        store.isRenamingSurveyLanguage = false;
      }
    }),
    deleteSurveyLanguage: flow(function* (
      surveyId: string,
      projectId: string,
      languageId: string,
    ) {
      store.isDeletingSurveyLanguage = true;
      store.deleteSurveyLanguageError = null;
      try {
        const apiClient = getAPIClient(store);
        const request = new DeleteSurveyLanguageRPC.Request(
          new LeoUUID(projectId),
          new LeoUUID(surveyId),
          new LeoUUID(languageId),
        );
        const {
          error,
        }: {
          response?: DeleteSurveyLanguageRPC.Response;
          error?: DeleteSurveyLanguageRPC.Errors.Errors;
        } =
          yield useDeleteSurveyLanguageRPCClientImpl(apiClient).execute(
            request,
          );

        if (error) {
          switch (error.code) {
            case DeleteSurveyLanguageRPC.RPCError.InvalidProjectId:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.InvalidProjectId;
              break;
            case DeleteSurveyLanguageRPC.RPCError.InvalidSurveyId:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.InvalidSurveyId;
              break;
            case DeleteSurveyLanguageRPC.RPCError.InvalidLanguageId:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.InvalidLanguageId;
              break;
            case DeleteSurveyLanguageRPC.RPCError.BaseLanguageCannotBeModified:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.BaseLanguageCannotBeModified;
              break;
            case DeleteSurveyLanguageRPC.RPCError.LanguageInUse:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.LanguageInUse;
              break;
            case DeleteSurveyLanguageRPC.RPCError.ProjectAlreadyArchived:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.ProjectAlreadyArchived;
              break;
            case DeleteSurveyLanguageRPC.RPCError.TranslationUpdateInProgress:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.TranslationUpdateInProgress;
              break;
            default:
              store.deleteSurveyLanguageError =
                DeleteSurveyLanguageError.Unexpected;
          }
        }
      } catch (e) {
        store.deleteSurveyLanguageError = DeleteSurveyLanguageError.Unexpected;
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occurred: ${e}`);
        }
      } finally {
        store.isDeletingSurveyLanguage = false;
      }
    }),
  }));

export const createTranslationsStore = (): Instance<
  typeof TranslationsStore
> => {
  return TranslationsStore.create({
    translationUploadStore: createTranslationUploadStore(),
  });
};
